<template>
	<div
		class="marketing-module-card"
		:class="{ 'marketing-module-card--visible': setup.visible }">
		<o-icon
			v-if="informationTooltip"
			v-tooltip="informationTooltip"
			class="marketing-module-card__attention text--warning"
			:class="{ 'marketing-module-card__attention--close': close }"
			font-icon="icon-interface-alert-information-circle"/>
		<span
			v-if="close"
			class="marketing-module-card__close"
			@click="setup.updateShowCardSettingAsync(false)"/>
		<div class="marketing-module-card__left">
			<h2 class="marketing-module-card__title">
				{{ title }}
			</h2>
			<div class="marketing-module-card__subtitle">
				<slot name="caption"/>
			</div>
			<div
				class="marketing-module-card__actions">
				<slot name="actions"/>
			</div>
			<div
				class="marketing-module-card__warning">
				<slot name="warn"/>
			</div>
		</div>
		<div
			:ref="setup._preview"
			class="marketing-module-card__right">
			<order-dashboard-home-order-item
				v-for="i in 3"
				:key="i"
				:auto-expand="i === 1"
				:order-item="setup.order"/>
			<div class="floating-circle">
				<o-icon font-icon="icon-travel-map-location-pin"/>
				<o-icon font-icon="icon-interface-calendar-mark"/>
				<o-icon font-icon="icon-user-single-neutral"/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import CarouselMarketingModuleSetupService from '@/setup/carousel/CarouselMarketingModuleSetupService';
import OrderDashboardHomeOrderItem from '@/components/order/OrderDashboardHomeOrderItem.vue';
import { inject } from 'vue';
const props = defineProps(CarouselMarketingModuleSetupService.props);
const _carousel = inject<OrionCarousel>('_carousel');
const setup = new CarouselMarketingModuleSetupService(props, _carousel);
</script>

<style lang="less">
@import '@/styles/variables';
@import '@/styles/mixins';

.marketing-module-card {
  transition: clip-path 0.6s, max-height 0.5s ease-out 0.5s, box-shadow 0.3s, transform 0.3s;
  position: relative;
  clip-path: circle(0% at 100% 0%);
  max-height: 0;

  > .orion-card__body{
    display: flex;
    flex-wrap: nowrap;
    padding: 1.25rem;
  }

  &__left {
    flex: 2;
		height: 100%;
    padding-top: 1.25rem;

    @media @tablet {
      padding-top: unset;
    }

    @media @desktop {
      flex: 3;
    }
  }

  &__right{
		position: absolute;
		top: -15rem;
    flex: 1;
    display: none;
		pointer-events: none;
		padding: 1.25rem;
		transform-style: preserve-3d;
		transform: rotateX(40deg) rotateZ(40deg) scale(0.7);
		z-index: -1;

		@media @tablet {
			right: -21.9rem;
      display: block;
		}

		@media @desktop {
			right: -11.9rem;
		}

		.floating-circle {
			position: absolute;
			top: 18.75rem;
			left: 0;
			border: 0.06rem dashed var(--grey-light);
			height: 12.5rem;
			width: 12.5rem;
			border-radius: 50%;
			transform: scale(0);
			z-index: -1;
			transition: transform .3s ease;
			animation: 25s linear rotateCircle infinite;

			.orion-icon {
				position: absolute;
				display: inline-block;
				font-size: 0.75rem;
				padding: 0.4rem;
				border-radius: 50%;
				background: white;
				color: var(--info);
				box-shadow: 0.25rem 0.25rem 0.6rem rgba(0,0,0,0.1);
				animation: 25s linear rotateIcon infinite;

				&:first-child {
					top: -0.6rem;
					left: 3.9rem;
				}

				&:nth-of-type(n + 2) {
					top: 3.1rem;
					left: -0.3rem;
				}

				&:nth-of-type(n + 3) {
					top: 0.75rem;
					left: 1.4rem;
				}
			}

		}

		.order-dashboard-home-order-item {
			margin-bottom: 1.25rem;
			transform-style: preserve-3d;

			.orion-card__body {
				transform-style: preserve-3d;
			}

			&__last-activity {
				transition: max-height .5s ease-in-out;
				z-index: -1;
				// transform-style: preserve-3d;

				.order-dashboard-home-history-item {
					&:nth-of-type(2n) {
						transform: translate3d(0, -2.5rem, 0);
					}

					&::before {
						position: absolute;
						content:"";
						height: 100%;
						width: 100%;
						background: rgba(0,0,0,0.13);
						transform: translate3d(0,0,0);
						filter: blur(0.4rem);
						transition: all 1s;
						border-radius: 0.3rem;
						opacity: 0;
					}
				}
			}

			&__last-activity--expanded {
				overflow: unset;
				transform-style: preserve-3d;

				.order-dashboard-home-order-item {
					&__container {
						background: transparent;
						transform-style: preserve-3d;
						transition: margin .2s;

						.order-dashboard-home-history-item {
							position: relative;
							transform-style: preserve-3d;
							background: var(--grey-lighter);
							transition: transform 1s;
							z-index: 2;

							&:first-of-type {
								transform: translate3d(-1.9rem, 0, 1.25rem);
								margin-bottom: 2.5rem;

								&::before {
									opacity: 1;
									transform: translate3d(0 ,0, -1.25rem);
								}

								.animation-loop & {
									animation: 2.4s ease-in-out 0s infinite alternate floating2;

									&::before {
										animation: 2.4s ease-in-out 0s infinite alternate adaptShadow2;
									}
								}
							}

							&:nth-of-type(2n) {
								transform: translate3d(-1.9rem, 0, 3.1rem);

								&::before {
									opacity: 0.6;
									transform: translate3d(0, 0, -3.1rem);
								}

								.animation-loop & {
									animation: 2s ease-in-out 0s infinite alternate floating;

									&::before {
										animation: 2s ease-in-out 0s infinite alternate adaptShadow;
									}
								}
							}
						}
					}
				}
			}
		}
  }

  &__title {
    font-size: 1.5rem;
    color: var(--grey-darker);
    margin-bottom: 0.6rem;
  }

  &__subtitle {
    margin-bottom: 2.5rem;
		font-size: 0.9rem;
  }

  &__attention {
    position: absolute;
    top: 1.25rem;
    right: 1.25rem;
    font-size: 1.25rem;

    &--close {
			right: 3.1rem;
    }
  }

  &__close {
    .close(1.25rem);
    position: absolute;
    top: 1.25rem;
    right: 1.25rem;
  }

	&__actions {
    display: flex;
    gap: 0.6rem;
    flex-direction: column;

    @media @tablet {
      flex-direction: row;
    }

    .orion-button + .orion-button {
      margin: unset;
    }
	}

	&__warning {
		margin-top: 0.6rem;
	}

  &--visible {
    transition: clip-path 1s ease-out, max-height 0.2s, box-shadow 0.3s, transform 0.3s;
    clip-path: circle(200% at 100% 5%);
    max-height: 37.5rem;
  }
}

@keyframes floating {
	from {
		transform: translate3d(-1.9rem, 0, 3.1rem);
	}
	to {
		transform: translate3d(-1.9rem, 0, 2.5rem);
	}
}

@keyframes adaptShadow {
	from {
		transform: translate3d(0, 0, -3rem);
		filter: blur(0.4rem);
		opacity: 0.6;
	}
	to {
		transform: translate3d(0, 0, -2.5rem);
		filter: blur(0.25rem);
		opacity: 1;
	}
}

@keyframes floating2 {
	from {
		transform: translate3d(-1.9rem, 0, 1.25rem);
	}
	to {
		transform: translate3d(-1.9rem, 0, 1.9rem);
	}
}

@keyframes adaptShadow2 {
	from {
		transform: translate3d(0, 0, -1.2rem);
		filter: blur(0.25rem);
		opacity: 1;
	}
	to {
		transform: translate3d(0, 0, -1.9rem);
		filter: blur(0.4rem);
		opacity: 0.6;
	}
}

@keyframes rotateCircle {
	from {
		transform: scale(2.3) rotate(70deg)
	}
	to {
		transform: scale(2.3) rotate(-290deg)
	}
}

@keyframes rotateIcon {
	0% {
		transform: rotate(-60deg);
	}
	100% {
		transform: rotate(300deg);
	}
}
</style>
